import React, { useState, useRef, useEffect } from 'react';
import './CustomDropdown.css';
import { IoChevronDown } from "react-icons/io5";

const CustomDropdown = ({
    options = [],
    value,
    onChange,
    placeholder = "Select",
    className = "",
    error = false,
    disabled = false,
    icon = null
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Helper to get label from value or object
    const getLabel = (option) => {
        if (!option) return "";
        return typeof option === 'string' ? option : (option.label || option.name);
    };

    const getValue = (option) => {
        if (!option) return "";
        return typeof option === 'string' ? option : (option.value || option.isoCode || option.name);
    };

    // Initialize search term when value changes externally
    useEffect(() => {
        if (value) {
            const selected = options.find(opt => getValue(opt) === value);
            if (selected) {
                setSearchTerm(getLabel(selected));
            } else {
                // If value exists but option not found (e.g. initial load before list populated), keep value string or empty
                // Ideally we shouldn't show raw codes, but if list is empty we can't do much. 
                // Let's assume options are always available or populated quickly.
            }
        } else {
            setSearchTerm("");
        }
    }, [value, options]); // Depend on options too in case they load later

    useEffect(() => {
        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);

                // Logic on Close:
                // 1. If search text is empty, clear the value.
                // 2. If search text matches the CURRENT value's label, do nothing.
                // 3. If search text is different (incomplete typing), revert to current value.

                if (searchTerm.trim() === "") {
                    if (value) onChange(""); // Text cleared -> Clear selection
                } else {
                    // Revert to selected value text if not empty
                    if (value) {
                        const selected = options.find(opt => getValue(opt) === value);
                        if (selected) setSearchTerm(getLabel(selected));
                    } else {
                        setSearchTerm("");
                    }
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [value, options, searchTerm, onChange]); // Added searchTerm and onChange to deps

    const handleSelect = (option) => {
        onChange(getValue(option));
        setSearchTerm(getLabel(option));
        setIsOpen(false);
    };

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
        if (!isOpen) setIsOpen(true);
    };

    const handleInputFocus = () => {
        if (!disabled) {
            setIsOpen(true);
            // Optional: Select text on focus for easy replacement?
            // e.target.select(); 
        }
    };

    const filteredOptions = options.filter(opt =>
        getLabel(opt).toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className={`custom-dropdown-container ${className}`} ref={dropdownRef}>
            <div
                className={`custom-dropdown-header ${isOpen ? 'is-open' : ''} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => {
                    if (!disabled) {
                        setIsOpen(true);
                        inputRef.current?.focus();
                    }
                }}
            >
                {/* If an icon is provided (like in BookSessionModal), render it */}
                {icon && <div className="dropdown-icon-wrapper" style={{ marginRight: '10px', display: 'flex' }}>{icon}</div>}

                <input
                    ref={inputRef}
                    type="text"
                    className="dropdown-search-input"
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    disabled={disabled}
                    readOnly={disabled}
                />

                <IoChevronDown 
                    className="dropdown-arrow" 
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!disabled) {
                            setIsOpen((prev) => {
                                if (!prev) setTimeout(() => inputRef.current?.focus(), 0);
                                return !prev;
                            });
                        }
                    }}
                />
            </div>

            {isOpen && (
                <div className="custom-dropdown-list">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => {
                            const optValue = getValue(option);
                            const optLabel = getLabel(option);
                            const isSelected = value === optValue;

                            return (
                                <div
                                    key={index}
                                    className={`custom-dropdown-option ${isSelected ? 'selected' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent header click from firing
                                        handleSelect(option);
                                    }}
                                >
                                    {optLabel}
                                    {/* Highlight match? (Optional complexity) */}
                                </div>
                            );
                        })
                    ) : (
                        <div className="custom-dropdown-option no-results">
                            No results found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomDropdown;
